import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchStatus, StatusApiError } from '../services/statusApi';
import type { CheckResult, JourneyResult, StatusPayload, StatusSummary } from '../types';

const REFRESH_MS = 60_000;

interface FetchError {
  status: number;
  message: string;
}

/** Derive a summary from the raw checks when the payload omits one. */
function deriveSummary(payload: StatusPayload): StatusSummary {
  if (payload.summary) {
    return payload.summary;
  }
  const total = payload.checks.length;
  const failed = payload.checks.filter((c) => !c.ok).length;
  return { total, passed: total - failed, failed, ok: failed === 0 };
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function relativeAge(iso: string | null): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.round(mins / 60);
  return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
}

function OkBadge({ ok }: { ok: boolean }) {
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ' +
        (ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800')
      }
    >
      {ok ? 'PASS' : 'FAIL'}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex items-center rounded-sm bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
      {type}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const cls =
    severity === 'critical'
      ? 'bg-red-50 text-red-700 ring-1 ring-red-200'
      : severity === 'high'
        ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
        : 'bg-slate-50 text-slate-500 ring-1 ring-slate-200';
  return (
    <span className={'inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-medium ' + cls}>
      {severity}
    </span>
  );
}

function AppGroup({ app, checks }: { app: string; checks: CheckResult[] }) {
  const failed = checks.filter((c) => !c.ok).length;
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
      <header className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">{app}</h3>
        <span
          className={
            'text-xs font-medium ' + (failed === 0 ? 'text-emerald-600' : 'text-red-600')
          }
        >
          {checks.length - failed}/{checks.length} passing
        </span>
      </header>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2 font-medium">Check</th>
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">HTTP</th>
              <th className="px-4 py-2 font-medium">Latency</th>
              <th className="px-4 py-2 font-medium">Severity</th>
              <th className="px-4 py-2 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {checks.map((c) => (
              <tr key={c.id} className={c.ok ? '' : 'bg-red-50/40'}>
                <td className="px-4 py-2 font-mono text-xs text-slate-700">{c.id}</td>
                <td className="px-4 py-2">
                  <TypeBadge type={c.type} />
                </td>
                <td className="px-4 py-2">
                  <OkBadge ok={c.ok} />
                </td>
                <td className="px-4 py-2 tabular-nums text-slate-600">
                  {c.status ?? '—'}
                </td>
                <td className="px-4 py-2 tabular-nums text-slate-600">{c.latencyMs} ms</td>
                <td className="px-4 py-2">
                  <SeverityBadge severity={c.severity} />
                </td>
                <td className="px-4 py-2 text-xs text-red-700">{c.error ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function JourneysSection({
  journeys,
  generatedAt,
}: {
  journeys: JourneyResult[];
  generatedAt?: string | null;
}) {
  const failed = journeys.filter((j) => j.status !== 'ok').length;
  const age = relativeAge(generatedAt ?? null);
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
      <header className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
            Journeys (L3 — Playwright)
          </h3>
          <p className="text-xs text-slate-400">
            Last run: {formatTimestamp(generatedAt ?? null)}
            {age ? ` (${age})` : ''}
          </p>
        </div>
        <span className={'text-xs font-medium ' + (failed === 0 ? 'text-emerald-600' : 'text-red-600')}>
          {journeys.length - failed}/{journeys.length} passing
        </span>
      </header>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2 font-medium">Journey</th>
              <th className="px-4 py-2 font-medium">App</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Duration</th>
              <th className="px-4 py-2 font-medium">Severity</th>
              <th className="px-4 py-2 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {journeys.map((j) => (
              <tr key={j.id} className={j.status === 'ok' ? '' : 'bg-red-50/40'}>
                <td className="px-4 py-2 font-mono text-xs text-slate-700">{j.id}</td>
                <td className="px-4 py-2 text-slate-600">{j.app ?? 'education'}</td>
                <td className="px-4 py-2">
                  <OkBadge ok={j.status === 'ok'} />
                </td>
                <td className="px-4 py-2 tabular-nums text-slate-600">{j.durationMs} ms</td>
                <td className="px-4 py-2">
                  <SeverityBadge severity={j.severity ?? 'critical'} />
                </td>
                <td className="px-4 py-2 text-xs text-red-700">
                  {j.error ?? ''}
                  {j.screenshot ? (
                    <span className="ml-1 text-slate-400">(screenshot captured)</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Banner({ summary, generatedAt }: { summary: StatusSummary; generatedAt: string | null }) {
  const allGood = summary.failed === 0 && summary.total > 0;
  const empty = summary.total === 0;
  const age = relativeAge(generatedAt);
  return (
    <div
      className={
        'rounded-xl border p-6 shadow-xs ' +
        (empty
          ? 'border-slate-200 bg-white'
          : allGood
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-red-200 bg-red-50')
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={
              'inline-block h-3 w-3 rounded-full ' +
              (empty ? 'bg-slate-400' : allGood ? 'bg-emerald-500' : 'bg-red-500')
            }
          />
          <h2
            className={
              'text-lg font-semibold ' +
              (empty ? 'text-slate-700' : allGood ? 'text-emerald-800' : 'text-red-800')
            }
          >
            {empty
              ? 'No health data yet'
              : allGood
                ? 'All systems healthy'
                : `${summary.failed} check${summary.failed === 1 ? '' : 's'} failing`}
          </h2>
        </div>
        <div className="text-right">
          <p
            className={
              'text-2xl font-bold tabular-nums ' +
              (empty ? 'text-slate-700' : allGood ? 'text-emerald-700' : 'text-red-700')
            }
          >
            {summary.passed}/{summary.total}
          </p>
          <p className="text-xs text-slate-500">checks passing</p>
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-500">
        Last run: {formatTimestamp(generatedAt)}
        {age ? ` (${age})` : ''}
      </p>
    </div>
  );
}

export default function Dashboard({ onSignOut, userName }: { onSignOut: () => void; userName?: string }) {
  const [data, setData] = useState<StatusPayload | null>(null);
  const [error, setError] = useState<FetchError | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (initial: boolean) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const payload = await fetchStatus();
      setData(payload);
      setError(null);
    } catch (err) {
      if (err instanceof StatusApiError) {
        setError({ status: err.status, message: err.message });
      } else {
        setError({ status: 0, message: String(err) });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
    const id = window.setInterval(() => void load(false), REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const byApp = new Map<string, CheckResult[]>();
    for (const c of data.checks) {
      const list = byApp.get(c.app) ?? [];
      list.push(c);
      byApp.set(c.app, list);
    }
    return [...byApp.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-navy text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">BVA Health Dashboard</h1>
            <p className="text-xs text-slate-300">func-bva-health synthetic monitoring</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => void load(false)}
              disabled={refreshing}
              className="rounded-md bg-white/10 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-white/20 disabled:opacity-50"
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            {userName ? <span className="hidden text-sm text-slate-300 sm:inline">{userName}</span> : null}
            <button
              onClick={onSignOut}
              className="rounded-md border border-white/30 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-white/10"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-6 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-navy" />
            <p className="mt-4 text-sm text-slate-500">Loading health status…</p>
          </div>
        ) : error && error.status === 403 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
            <h2 className="text-lg font-semibold text-amber-800">Access denied</h2>
            <p className="mt-2 text-sm text-amber-700">
              Your account is not authorized to view the health dashboard.
            </p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
            <h2 className="text-lg font-semibold text-red-800">Could not load health status</h2>
            <p className="mt-2 text-sm text-red-700">{error.message}</p>
            <button
              onClick={() => void load(true)}
              className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        ) : data ? (
          <>
            <Banner summary={deriveSummary(data)} generatedAt={data.generatedAt} />
            {data.journeys && data.journeys.length > 0 ? (
              <JourneysSection
                journeys={data.journeys}
                generatedAt={data.journeysGeneratedAt}
              />
            ) : null}
            {grouped.length === 0 ? (
              <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                No checks have been recorded yet. The runner persists results on its next run
                (every 5 minutes).
              </p>
            ) : (
              <div className="space-y-6">
                {grouped.map(([app, checks]) => (
                  <AppGroup key={app} app={app} checks={checks} />
                ))}
              </div>
            )}
            <p className="text-center text-xs text-slate-400">Auto-refreshes every 60 seconds.</p>
          </>
        ) : null}
      </main>
    </div>
  );
}
